import { AttendeeUserDefault } from '@/types/all-attendees.types';
import type { ImportedProfile } from '@/types/passcode-list.types';
import TableData from '@layouts/layout-table/layout-table-data';
import TableRow from '@layouts/layout-table/layout-table-row';
import Button from '@ui/buttons/button/button';
import Checkbox from '@ui/checkbox/checkbox';
import type { ColumnDefinition } from './profile-column-definitions';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';
import Text from '@ui/text/text/text';
import { TooltipPortalPosition } from '@ui/tooltip-portal/tooltip-portal-types';
import { RegFieldsEnum } from '@/types/registration.types';
import Container from '@ui/container/container';
import ProfileListCell from './profile-list-cell';
import { useMemo } from 'react';

type ProfileListRowProps = {
  user: AttendeeUserDefault;
  visibleColumns: ColumnDefinition[];
  selected: boolean;
  onSelect: (profile: ImportedProfile, isOn: boolean) => void;
  onAddClick: (user: AttendeeUserDefault) => void;
};

const ProfileListRow = ({ user, visibleColumns, selected, onSelect, onAddClick }: ProfileListRowProps) => {
  const importedProfile: ImportedProfile = useMemo(
    () => ({
      '1': user?.[RegFieldsEnum.first_name] ?? '',
      '2': user?.[RegFieldsEnum.last_name] ?? '',
      '3': user.email,
    }),
    [user]
  );

  return useMemo(
    () => (
      <TableRow
        backgroundColor={selected ? 'var(--global-cont-1)' : 'transparent'}
        hoverBackgroundColor="var(--global-cont-1)"
      >
        <TableData style={{ width: 64, minWidth: 64 }}>
          <Container className="padding-h-32">
            <Checkbox
              value={user.email}
              checked={selected}
              onChange={(_value, isOn) => onSelect(importedProfile, isOn)}
              size={16}
              selectedIconName={ICONS.ROUND_CHECKBOX_SELECTED}
              unselectedIconName={ICONS.ROUND_CHECKBOX_UNSELECTED}
            />
          </Container>
        </TableData>
        {visibleColumns.map((col) => (
          <ProfileListCell key={col.id} column={col} user={user} />
        ))}
        <TableData>
          <TooltipPortal
            tooltipContent={
              <Text maxWidth="100px" clamp={1} className="P2" design="GlobalIcoText1">
                Add to list
              </Text>
            }
            position={TooltipPortalPosition.TOP}
          >
            <Button className="no-style" onClick={() => onAddClick(user)}>
              <Icon name={ICONS.ADD} size={12} />
            </Button>
          </TooltipPortal>
        </TableData>
      </TableRow>
    ),
    [importedProfile, onAddClick, onSelect, selected, user, visibleColumns]
  );
};

export default ProfileListRow;
